import { useState } from "react"
import { useApolloClient } from "@apollo/client"
import { errorToast, toast } from "@/v2-ui/toast"
import {
  pickedChanges,
  ruleChanges,
  toPickedMap,
} from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.compose.changes"
import {
  ruleSignature,
  toRuleInput,
  type FeatureGroupPickedLike,
  type FeatureGroupRuleLike,
} from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.compose.utils"
import { useFeatureGroupFeaturesSetMutation } from "@/v2-backoffice-client/license/featureGroup/license.featureGroup.features.set.mutation"
import { useFeatureGroupRuleOptionsQuery } from "@/v2-backoffice-client/license/featureGroup/license.featureGroup.ruleOptions.query"
import { useFeatureGroupRulesSetMutation } from "@/v2-backoffice-client/license/featureGroup/license.featureGroup.rules.set.mutation"
import { evictFeatureGroupQueries } from "@/v2-backoffice-client/license/license.utils"
import type { LicenseFeatureGroupChange } from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupComposeFooter"
import type { FeatureGroupFragment } from "@/v2-backoffice-client/license/featureGroup/__types__/license.featureGroup.fragment"

export type FeatureGroupDraftWrite = {
  featureCodes: string[]
  rules: ReturnType<typeof toRuleInput>[]
}

export type UseFeatureGroupDraftInput = {
  featureGroup: Pick<FeatureGroupFragment, "Id" | "Name">
  /** As persisted; seed the drafts. The caller remounts when either changes. */
  pickedFeatures: readonly FeatureGroupPickedLike[]
  rules: readonly FeatureGroupRuleLike[]
  /**
   * Who writes. By default the package's two replace-the-set mutations; a
   * caller with more to write in the same transaction (the customer lens:
   * the closed classes with the list) hands in its own write, which always
   * receives the complete draft and does its own eviction. Toasts are the
   * hook's either way.
   */
  write?: (draft: FeatureGroupDraftWrite) => Promise<unknown>
  /** Pending edits the caller tracks beside the drafts (the customer lens's modes). */
  extraChanges?: readonly LicenseFeatureGroupChange[]
  /** Called on Reset, so the caller drops its own draft too. */
  onReset?: () => void
  /** The toasts' subject: "Package" by default. */
  subject?: string
}

/**
 * The package's two drafts — what is picked by hand, and the rules — as one
 * owner every section edits and the footer writes. Nothing is written until
 * `save`; `reset` drops both drafts back to what is persisted; `changes`
 * lists what differs in the meantime. Save sends each changed draft as a
 * complete set (replace-the-set, no lost-update window) and evicts
 * afterwards, so the caller's key reseeds the drafts from the server.
 */
export function useFeatureGroupDraft(input: UseFeatureGroupDraftInput) {
  const {
    featureGroup,
    pickedFeatures,
    rules,
    write,
    extraChanges = [],
    onReset,
    subject = "Package",
  } = input

  const [pickedByCode, setPickedByCode] = useState(() =>
    toPickedMap(pickedFeatures),
  )
  const [ruleDraft, setRuleDraft] =
    useState<readonly FeatureGroupRuleLike[]>(rules)
  const persistedByCode = toPickedMap(pickedFeatures)

  // The class and module labels for the change list; the same catalog read
  // the sections make, answered from the cache.
  const { data: optionData } = useFeatureGroupRuleOptionsQuery()
  const options = optionData?.getFeatureGroupRuleOptions ?? []

  const client = useApolloClient()
  const [setFeatures] = useFeatureGroupFeaturesSetMutation()
  const [setRules] = useFeatureGroupRulesSetMutation()

  const pickedDiff = pickedChanges(persistedByCode, pickedByCode)
  const hasRuleChanges = ruleSignature(rules) !== ruleSignature(ruleDraft)

  // Functional update, so a preset that clears a card and then picks a
  // sibling's selection in two calls composes instead of the second call
  // overwriting the first.
  function setPicked(
    features: readonly FeatureGroupPickedLike[],
    picked: boolean,
  ) {
    setPickedByCode((current) => {
      const next = new Map(current)
      for (const feature of features) {
        if (picked) next.set(feature.Code, feature)
        else next.delete(feature.Code)
      }
      return next
    })
  }

  const [isWriting, setIsWriting] = useState(false)

  function reset() {
    setPickedByCode(toPickedMap(pickedFeatures))
    setRuleDraft(rules)
    onReset?.()
  }

  // The package's own write: each changed draft as a complete set
  // (replace-the-set, no lost-update window), then the eviction that lets
  // the caller's key reseed from the server, whichever writes landed.
  async function writePackage(draft: FeatureGroupDraftWrite) {
    const writes: Promise<unknown>[] = []
    if (pickedDiff.length > 0) {
      writes.push(
        setFeatures({
          variables: {
            featureGroupId: featureGroup.Id,
            featureCodes: draft.featureCodes,
          },
        }),
      )
    }
    if (hasRuleChanges) {
      writes.push(
        setRules({
          variables: { featureGroupId: featureGroup.Id, rules: draft.rules },
        }),
      )
    }
    try {
      await Promise.all(writes)
    } finally {
      evictFeatureGroupQueries(client.cache)
    }
  }

  async function save() {
    setIsWriting(true)
    try {
      await (write ?? writePackage)({
        featureCodes: [...pickedByCode.keys()],
        rules: ruleDraft.map(toRuleInput),
      })
      toast({
        variant: "success",
        title: `${subject} saved`,
        description: `${featureGroup.Name} was updated.`,
      })
    } catch (error) {
      errorToast(error as Error, {
        title: `Failed to save the ${subject.toLowerCase()}`,
      })
    } finally {
      setIsWriting(false)
    }
  }

  return {
    pickedFeatures: [...pickedByCode.values()],
    persistedFeatures: pickedFeatures,
    ruleDraft,
    setRuleDraft,
    setPicked,
    isPicked: (code: string) => pickedByCode.has(code),
    wasPicked: (code: string) => persistedByCode.has(code),
    changes: [
      ...extraChanges,
      ...ruleChanges(options, rules, ruleDraft),
      ...pickedDiff,
    ],
    isSaving: isWriting,
    save,
    reset,
  }
}
